import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Brain, Save, RotateCcw, Plus, Trash2, AlertTriangle, Loader2, Sparkles, MessageSquareWarning } from 'lucide-react';

const MODELS = [
  { value: 'gpt-4o',      label: 'GPT-4o (default, smartest)' },
  { value: 'gpt-4o-mini', label: 'GPT-4o mini (faster, cheaper)' },
];

const SUB_TABS = [
  { id: 'prompt',   label: 'System Prompt', icon: Brain },
  { id: 'behavior', label: 'Behavior',      icon: Sparkles },
  { id: 'crisis',   label: 'Crisis Words',  icon: AlertTriangle },
  { id: 'events',   label: 'Crisis Log',    icon: MessageSquareWarning },
];

export default function AICoachEditor() {
  const [cfg, setCfg] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [tab, setTab] = useState('prompt');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/ai-config');
      setCfg(r.data.config);
      setDefaults(r.data.defaults);
    } catch (e) {
      toast.error('Failed to load AI Coach config.');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await api.put('/admin/ai-config', patch);
      setCfg(r.data.config);
      toast.success('AI Coach updated.');
    } catch (e) {
      toast.error('Save failed.', { description: String(e?.response?.data?.detail || e.message) });
    } finally { setSaving(false); }
  };

  const reset = async (section) => {
    if (!window.confirm(`Reset ${section === 'all' ? 'EVERYTHING' : section} to defaults?`)) return;
    try {
      const r = await api.post('/admin/ai-config/reset', { sections: [section] });
      setCfg(r.data.config);
      toast.success('Reset done.');
    } catch (e) {
      toast.error('Reset failed.');
    }
  };

  if (loading || !cfg) {
    return <div className="py-12 text-center text-muted-kc" data-testid="ai-loading"><Loader2 className="inline animate-spin mr-2" size={16}/> Loading…</div>;
  }

  return (
    <div data-testid="ai-coach-editor">
      <div className="flex gap-1 mb-5 overflow-x-auto">
        {SUB_TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-1.5 text-xs uppercase tracking-wider font-semibold rounded-full whitespace-nowrap inline-flex items-center gap-1.5 transition-colors ${tab === t.id ? 'bg-ink text-white' : 'text-muted-kc hover:bg-surface-2 border border-kc'}`} data-testid={`ai-tab-${t.id}`}><Icon size={12}/> {t.label}</button>
          );
        })}
      </div>

      {tab === 'prompt'   && <PromptPanel    cfg={cfg} defaults={defaults} save={save} reset={reset} saving={saving}/>}
      {tab === 'behavior' && <BehaviorPanel  cfg={cfg} save={save} saving={saving}/>}
      {tab === 'crisis'   && <CrisisPanel    cfg={cfg} defaults={defaults} save={save} reset={reset} saving={saving}/>}
      {tab === 'events'   && <CrisisEvents/>}
    </div>
  );
}

function PromptPanel({ cfg, defaults, save, reset, saving }) {
  const [text, setText] = useState(cfg.system_prompt || '');
  return (
    <div className="space-y-3" data-testid="ai-prompt-panel">
      <p className="text-sm text-muted-kc">This is the instructions the AI follows on every reply. Edit freely — keep it in your voice. Length matters but keep under ~12,000 characters.</p>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={22}
        className="w-full px-3 py-3 rounded-[var(--kc-radius-sm)] border border-kc bg-white font-mono text-sm leading-relaxed focus:outline-none focus:border-[color:var(--kc-gold)]"
        data-testid="ai-prompt-textarea"
      />
      <div className="flex justify-between text-xs text-muted-kc"><span>{text.length} / 12000 chars</span></div>
      <div className="flex justify-between gap-2">
        <button onClick={() => reset('system_prompt')} className="h-10 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-sm text-muted-kc hover:bg-surface-2 border border-kc" data-testid="ai-prompt-reset"><RotateCcw size={14}/> Reset to default</button>
        <button onClick={() => setText(defaults.system_prompt)} className="h-10 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-sm text-muted-kc hover:bg-surface-2 border border-kc">Load default (preview)</button>
        <button onClick={() => save({ system_prompt: text })} disabled={saving} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold disabled:opacity-50" data-testid="ai-prompt-save">{saving ? <Loader2 size={16} className="animate-spin"/> : <Save size={16}/>} Save prompt</button>
      </div>
    </div>
  );
}

function BehaviorPanel({ cfg, save, saving }) {
  const [memory, setMemory] = useState(cfg.memory_window || 14);
  const [temp, setTemp]     = useState(cfg.temperature ?? 0.8);
  const [signOff, setSignOff] = useState(!!cfg.sign_off_enabled);
  const [model, setModel]   = useState(cfg.model || 'gpt-4o');
  return (
    <div className="space-y-4" data-testid="ai-behavior-panel">
      <p className="text-sm text-muted-kc">Tune how the AI responds. These take effect immediately on next message.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-white border border-kc rounded-[var(--kc-radius-md)]">
          <label className="text-[10px] uppercase tracking-wider text-muted-kc font-semibold">Memory window (prior messages)</label>
          <input type="number" min={0} max={50} value={memory} onChange={(e) => setMemory(Math.max(0, Math.min(50, parseInt(e.target.value) || 0)))} className="mt-2 w-24 h-10 px-3 rounded-[var(--kc-radius-sm)] border border-kc bg-white font-display text-xl tabular-nums" data-testid="ai-memory-input"/>
          <div className="text-xs text-muted-kc mt-2">How many prior messages get fed back into each reply. 14 is balanced. 0 = no memory.</div>
        </div>
        <div className="p-4 bg-white border border-kc rounded-[var(--kc-radius-md)]">
          <label className="text-[10px] uppercase tracking-wider text-muted-kc font-semibold">Creativity (temperature)</label>
          <input type="number" step={0.1} min={0} max={2} value={temp} onChange={(e) => setTemp(Math.max(0, Math.min(2, parseFloat(e.target.value) || 0)))} className="mt-2 w-24 h-10 px-3 rounded-[var(--kc-radius-sm)] border border-kc bg-white font-display text-xl tabular-nums" data-testid="ai-temp-input"/>
          <div className="text-xs text-muted-kc mt-2">0 = boring & predictable. 0.8 = balanced. 1.2+ = wild.</div>
        </div>
        <div className="p-4 bg-white border border-kc rounded-[var(--kc-radius-md)] md:col-span-2">
          <label className="text-[10px] uppercase tracking-wider text-muted-kc font-semibold">Model</label>
          <select value={model} onChange={(e) => setModel(e.target.value)} className="mt-2 w-full h-10 px-3 rounded-[var(--kc-radius-sm)] border border-kc bg-white text-sm" data-testid="ai-model-select">
            {MODELS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <div className="p-4 bg-white border border-kc rounded-[var(--kc-radius-md)] md:col-span-2 flex items-center justify-between gap-4">
          <div>
            <div className="font-semibold">Allow recurring sign-off line</div>
            <div className="text-xs text-muted-kc mt-0.5">When OFF, the AI is told NOT to keep ending messages with “Live Life. Do Good. Be Happy.” or any tagline. Recommended OFF for natural conversation.</div>
          </div>
          <button onClick={() => setSignOff(!signOff)} className={`relative w-12 h-7 rounded-full transition-colors flex-shrink-0 ${signOff ? 'bg-[color:var(--kc-gold)]' : 'bg-gray-300'}`} data-testid="ai-signoff-toggle">
            <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all ${signOff ? 'left-5' : 'left-0.5'}`}/>
          </button>
        </div>
      </div>
      <div className="flex justify-end">
        <button onClick={() => save({ memory_window: memory, temperature: temp, sign_off_enabled: signOff, model })} disabled={saving} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold disabled:opacity-50" data-testid="ai-behavior-save">{saving ? <Loader2 size={16} className="animate-spin"/> : <Save size={16}/>} Save behavior</button>
      </div>
    </div>
  );
}

function CrisisPanel({ cfg, defaults, save, reset, saving }) {
  const [words, setWords]     = useState((cfg.crisis_keywords || []).join('\n'));
  const [response, setResp]   = useState(cfg.crisis_response || '');
  const list = words.split('\n').map(s => s.trim()).filter(Boolean);
  return (
    <div className="space-y-4" data-testid="ai-crisis-panel">
      <div className="p-4 rounded-[var(--kc-radius-md)] bg-red-50 border border-red-200">
        <div className="font-semibold text-[color:var(--kc-danger)] flex items-center gap-1.5 text-sm"><AlertTriangle size={14}/> Crisis trigger words</div>
        <p className="text-xs text-ink-2 mt-1">When ANY of these phrases appear in a user's message (case-insensitive), the AI short-circuits its normal reply and shows the crisis response below. The event is logged for your review in the “Crisis Log” tab.</p>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] uppercase tracking-wider text-muted-kc font-semibold">Trigger words — one per line</label>
          <span className="text-xs text-muted-kc">{list.length} active</span>
        </div>
        <textarea value={words} onChange={(e) => setWords(e.target.value)} rows={12} className="w-full px-3 py-3 rounded-[var(--kc-radius-sm)] border border-kc bg-white font-mono text-sm focus:outline-none focus:border-[color:var(--kc-gold)]" data-testid="ai-crisis-words" placeholder={'suicide\nkill myself\nend my life\n…'}/>
        <div className="text-xs text-muted-kc mt-1">Phrases match anywhere in the text (whitespace collapsed). Use short, specific phrases.</div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-muted-kc font-semibold">Crisis response (Markdown OK)</label>
        <textarea value={response} onChange={(e) => setResp(e.target.value)} rows={8} className="mt-1 w-full px-3 py-3 rounded-[var(--kc-radius-sm)] border border-kc bg-white text-sm leading-relaxed focus:outline-none focus:border-[color:var(--kc-gold)]" data-testid="ai-crisis-response"/>
      </div>
      <div className="flex justify-between gap-2">
        <div className="flex gap-2">
          <button onClick={() => reset('crisis_keywords')} className="h-10 px-3 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-sm text-muted-kc hover:bg-surface-2 border border-kc"><RotateCcw size={14}/> Reset words</button>
          <button onClick={() => reset('crisis_response')} className="h-10 px-3 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] text-sm text-muted-kc hover:bg-surface-2 border border-kc"><RotateCcw size={14}/> Reset response</button>
        </div>
        <button onClick={() => save({ crisis_keywords: list, crisis_response: response })} disabled={saving} className="h-11 px-5 inline-flex items-center gap-2 rounded-[var(--kc-radius-sm)] bg-ink text-white font-semibold disabled:opacity-50" data-testid="ai-crisis-save">{saving ? <Loader2 size={16} className="animate-spin"/> : <Save size={16}/>} Save crisis settings</button>
      </div>
    </div>
  );
}

function CrisisEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get('/admin/crisis-events?limit=100').then(r => setEvents(r.data || [])).finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="py-8 text-center text-muted-kc"><Loader2 className="inline animate-spin mr-2" size={14}/> Loading…</div>;
  return (
    <div data-testid="ai-crisis-events">
      <p className="text-sm text-muted-kc mb-4">Every time a user's message hits a crisis trigger, it lands here for review. Most recent first.</p>
      {events.length === 0 ? (
        <div className="py-12 text-center bg-surface-2 rounded-[var(--kc-radius-md)] border border-kc">
          <MessageSquareWarning className="mx-auto text-muted-kc mb-3" size={28}/>
          <h3 className="font-display uppercase text-xl">No crisis events yet.</h3>
          <p className="text-sm text-muted-kc mt-1">When a trigger word is detected, you'll see it here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {events.map(e => (
            <div key={e.id} className="p-4 bg-white border border-kc rounded-[var(--kc-radius-md)] border-l-4 border-l-[color:var(--kc-danger)]" data-testid={`ai-event-${e.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-xs uppercase tracking-wider text-[color:var(--kc-danger)] font-semibold">{e.matched_keyword}</div>
                <div className="text-xs text-muted-kc">{new Date(e.created_at).toLocaleString()}</div>
              </div>
              <div className="mt-2 text-sm text-ink whitespace-pre-wrap">{e.message}</div>
              <div className="mt-2 text-xs text-muted-kc">User ID: {e.user_id}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
